/**
 * Utilities for adding joint checking parameters to enrollment URLs.
 * Mirrors the referral pattern in referralHelpers.ts.
 */

import { getCookie } from 'cookies-next/client';
import { JOINT_CHECKING_TOKEN_COOKIE } from './cookies';
import { isBrowser } from './environment';
import { getUrlParseBase } from './url';
import type { EnrollmentTokenSources } from './referralHelpers';

const JOINT_CHECKING_PAGE_REGEX = /^\/joint-checking\/([^/]+)/;
// Static routes under /joint-checking/ are not invite tokens.
const RESERVED_JOINT_CHECKING_SLUGS = new Set(['invitation-expired']);

export function isJointCheckingPage(path: string): boolean {
  return JOINT_CHECKING_PAGE_REGEX.test(path);
}

// Pure so the server resolves the same token the client will. Precedence: path, cookie, query.
export function resolveJointCheckingToken({
  pathname,
  cookieValue,
  queryParams,
}: EnrollmentTokenSources): string | null {
  const match = pathname.match(JOINT_CHECKING_PAGE_REGEX);
  if (match?.[1] && !RESERVED_JOINT_CHECKING_SLUGS.has(match[1])) {
    return match[1];
  }

  if (cookieValue) {
    return cookieValue;
  }

  return queryParams.get('joint_account_token') || null;
}

function getJointCheckingToken(): string | null {
  if (!isBrowser()) {
    return null;
  }

  return resolveJointCheckingToken({
    pathname: window.location.pathname,
    cookieValue: getCookie(JOINT_CHECKING_TOKEN_COOKIE),
    queryParams: new URLSearchParams(window.location.search),
  });
}

// Omit `token` to resolve it from the browser.
export function addJointCheckingParamsToUrl(url: string, token: string | null = getJointCheckingToken()): string {
  if (!url.includes('/enroll')) {
    return url;
  }

  if (!token) {
    return url;
  }

  try {
    const urlObj = new URL(url, getUrlParseBase());
    urlObj.searchParams.set('ad', 'ch_joint_account');
    urlObj.searchParams.set('joint_account_token', token);

    if (url.startsWith('/')) {
      return urlObj.pathname + urlObj.search;
    }
    return urlObj.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}ad=ch_joint_account&joint_account_token=${token}`;
  }
}
